import React, { useEffect, useState } from "react";
import api from "../../../api/axios";
import Swal from "sweetalert2";

export default function SalaryStructure() {
  const [structures, setStructures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [usingSample, setUsingSample] = useState(false);

  const [expanded, setExpanded] = useState({});
  const [showAddForm, setShowAddForm] = useState(false);
  const [editId, setEditId] = useState(null);

  // payroll items are loaded but never shown to user
  const [payrollItems, setPayrollItems] = useState([]); // { id, code, name, type, value_type }
  const [saving, setSaving] = useState(false);

  const generateCode = (name) => {
    if (!name) return "";
    return name.trim().replace(/\s+/g, "_").toUpperCase();
  };

  const blankForm = {
    code: "",
    name: "",
    description: "",
    min_ctc: "",
    max_ctc: "",
    effective_from: "",
    components: [],
  };
  const [form, setForm] = useState(blankForm);

  useEffect(() => {
    loadPayrollItems();
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const formatDate = (date) => {
    if (!date) return "";
    return String(date).split("T")[0];
  };

  /* -----------------------
     Load payroll items (API) - used internally for auto-detection and percentage_of
  ------------------------*/
  const loadPayrollItems = async () => {
    try {
      const res = await api.get("hrms/prms/items");
      const items = (res?.data?.data?.data || []).map((i) => ({
        id: i.id,
        code: (i.code || i.name || `ITEM_${i.id}`).toString(),
        name: (i.name || i.code || `Item ${i.id}`).toString(),
        type: i.type || "earning", // important for filtering percentage_of
        value_type: i.value_type || null,
      }));
      if (!items.length) throw new Error("No items");
      setPayrollItems(items);
    } catch (err) {
      console.warn("Payroll items load failed, using sample", err);
      setPayrollItems([
        {
          id: 1,
          code: "BASIC",
          name: "Basic",
          type: "earning",
          value_type: "fixed",
        },
        {
          id: 2,
          code: "HRA",
          name: "HRA",
          type: "earning",
          value_type: "percent",
        },
        {
          id: 3,
          code: "ALLOW",
          name: "Allowance",
          type: "earning",
          value_type: "fixed",
        },
        {
          id: 4,
          code: "PF",
          name: "Provident Fund",
          type: "deduction",
          value_type: "percent",
        },
      ]);
    }
  };

  /* -----------------------
     Load salary structures
  ------------------------*/
  const loadData = async () => {
    setLoading(true);
    try {
      const res = await api.get("hrms/prms/salary-structures");
      const data =
        res?.data?.data?.data || res?.data?.structures || res?.data || [];
      setStructures(Array.isArray(data) ? data : []);
      setUsingSample(false);
    } catch (err) {
      console.warn("Structures API failed, loading sample", err);
      setStructures([
        {
          id: 1,
          name: "Level 1",
          description: "Entry level",
          min_ctc: 1200000,
          max_ctc: 1800000,
          effective_from: "2024-04-01",
          components: [
            {
              component_name: "Basic",
              component_type: "earning",
              payroll_item_id: 1,
              calculation_type: "percentage",
              percentage_of: "ctc",
              value: 40,
              sequence: 1,
            },
            {
              component_name: "PF",
              component_type: "deduction",
              payroll_item_id: 4,
              calculation_type: "percentage",
              percentage_of: "BASIC",
              value: 12,
              sequence: 2,
            },
          ],
        },
      ]);
      setUsingSample(true);
    }
    setLoading(false);
  };

  const toggleExpand = (id) => setExpanded((p) => ({ ...p, [id]: !p[id] }));

  const handleFormChange = (field, value) =>
    setForm((p) => ({ ...p, [field]: value }));

  /* -----------------------
     Helpers: find payroll item by name/code (case-insensitive)
  ------------------------*/
  const findPayrollItemByNameOrCode = (text) => {
    if (!text) return null;
    const t = String(text).trim().toLowerCase();
    return (
      payrollItems.find(
        (pi) =>
          (pi.name && pi.name.toLowerCase() === t) ||
          (pi.code && pi.code.toLowerCase() === t)
      ) || null
    );
  };

  /* -----------------------
     Components management
  ------------------------*/
  const addComponent = () => {
    setForm((p) => {
      const seq = p.components.length
        ? Math.max(...p.components.map((c) => c.sequence || 0)) + 1
        : 1;
      return {
        ...p,
        components: [
          ...p.components,
          {
            component_name: "",
            component_type: "earning",
            payroll_item_id: null,
            payroll_item_code: "",
            calculation_type: "percentage",
            percentage_of: "",
            value: "",
            sequence: seq,
            meta: {},
          },
        ],
      };
    });
  };

  const removeComponent = (index) => {
    setForm((p) => {
      const copy = [...p.components];
      copy.splice(index, 1);
      return { ...p, components: copy };
    });
  };

  const updateComponent = (index, field, value) => {
    setForm((p) => {
      const copy = p.components.map((c, i) =>
        i === index ? { ...c, [field]: value } : c
      );
      return { ...p, components: copy };
    });
  };

  /* -----------------------
     When user types component_name:
     - if it matches payroll item name/code -> set payroll_item_id & component_type internally
     - otherwise clear payroll_item_id (backend will create)
  ------------------------*/
  const handleComponentNameChange = (index, text) => {
    const trimmed = text ?? "";
    const matched = findPayrollItemByNameOrCode(trimmed);

    setForm((p) => {
      const copy = p.components.map((c, i) => {
        if (i !== index) return c;
        if (matched) {
          return {
            ...c,
            component_name: matched.name,
            payroll_item_id: matched.id,
            payroll_item_code: matched.code,
            component_type: matched.type,
          };
        } else {
          return {
            ...c,
            component_name: trimmed,
            payroll_item_id: null,
            payroll_item_code: "",
            // leave component_type as user had it (default earning)
          };
        }
      });
      return { ...p, components: copy };
    });
  };

  /* -----------------------
     Save Structure
  ------------------------*/
  const saveStructure = async () => {
    if (!form.name?.trim()) {
      Swal.fire("Validation", "Name is required", "warning");
      return;
    }
    if (!form.min_ctc || !form.max_ctc) {
      Swal.fire("Validation", "Min and Max CTC required", "warning");
      return;
    }
    if (!form.effective_from) {
      Swal.fire("Validation", "Effective From is required", "warning");
      return;
    }
    if (!form.components.length) {
      Swal.fire("Validation", "Add at least one component", "warning");
      return;
    }

    for (let i = 0; i < form.components.length; i++) {
      const c = form.components[i];
      if (!c.component_name?.trim()) {
        Swal.fire("Validation", `Component #${i + 1} needs a name`, "warning");
        return;
      }
      if (!c.payroll_item_id && !c.component_type) {
        Swal.fire(
          "Validation",
          `Component #${
            i + 1
          } must have a component type when payroll item is not selected`,
          "warning"
        );
        return;
      }
      if (!c.value && c.value !== 0) {
        Swal.fire("Validation", `Component #${i + 1} needs a value`, "warning");
        return;
      }
      if (c.calculation_type === "percentage" && !c.percentage_of) {
        Swal.fire(
          "Validation",
          `Component #${
            i + 1
          } requires "Percentage Of" when calculation type is percentage`,
          "warning"
        );
        return;
      }
    }

    const payload = {
      code: generateCode(form.name),
      name: form.name,
      description: form.description,
      min_ctc: Number(form.min_ctc),
      max_ctc: Number(form.max_ctc),
      effective_from: form.effective_from,
      components: form.components.map((c) => ({
        payroll_item_id: c.payroll_item_id ? Number(c.payroll_item_id) : null,
        component_name: c.component_name,
        component_type: c.component_type || "earning",
        calculation_type: c.calculation_type,
        percentage_of:
          c.calculation_type === "percentage" ? c.percentage_of : null,
        value: Number(c.value),
        sequence: c.sequence,
        value_type: c.calculation_type === "percentage" ? "percent" : "fixed",
        meta: c.meta ?? {},
      })),
    };

    setSaving(true);
    try {
      if (editId) {
        await api.put(`hrms/prms/salary-structures/${editId}`, payload);
        Swal.fire("Updated", "Structure updated successfully", "success");
      } else {
        await api.post("hrms/prms/salary-structures", payload);
        Swal.fire("Saved", "Structure added successfully", "success");
      }
      setShowAddForm(false);
      setEditId(null);
      setForm(blankForm);
      await loadData();
    } catch (err) {
      console.warn("Save failed, using local fallback", err);
      if (editId) {
        setStructures((prev) =>
          prev.map((s) => (s.id === editId ? { ...s, ...payload } : s))
        );
        Swal.fire("Saved Locally", "Updated locally (API not ready)", "info");
      } else {
        setStructures((prev) => [...prev, { ...payload, id: prev.length + 1 }]);
        Swal.fire("Saved Locally", "Added locally (API not ready)", "info");
      }
      setShowAddForm(false);
      setEditId(null);
      setForm(blankForm);
    } finally {
      setSaving(false);
    }
  };

  /* -----------------------
     Start edit
  ------------------------*/
  const startEdit = (s) => {
    setEditId(s.id);
    setForm({
      name: s.name || "",
      description: s.description || "",
      min_ctc: s.min_ctc ?? "",
      max_ctc: s.max_ctc ?? "",
      effective_from: s.effective_from || "",
      components:
        Array.isArray(s.components) && s.components.length
          ? s.components.map((c, idx) => ({
              component_name: c.component_name ?? "",
              component_type: c.component_type ?? "earning",
              payroll_item_id: c.payroll_item_id ?? null,
              payroll_item_code: c.payroll_item_code ?? "",
              calculation_type: c.calculation_type || "percentage",
              percentage_of: c.percentage_of ?? "",
              value: c.value ?? "",
              sequence: c.sequence ?? idx + 1,
              meta: c.meta ?? {},
            }))
          : [
              {
                component_name: "",
                component_type: "earning",
                payroll_item_id: null,
                payroll_item_code: "",
                calculation_type: "percentage",
                percentage_of: "",
                value: "",
                sequence: 1,
              },
            ],
    });
    setShowAddForm(true);
    setExpanded((p) => ({ ...p, [s.id]: true }));
  };

  const confirmDelete = (s) => {
    Swal.fire({
      title: "Delete structure?",
      text: `Delete "${s.name}"? This cannot be undone.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Delete",
      cancelButtonText: "Cancel",
    }).then(async (res) => {
      if (res.isConfirmed) {
        try {
          await api.delete(`hrms/prms/salary-structures/${s.id}`);
          setStructures((prev) => prev.filter((x) => x.id !== s.id));
          Swal.fire("Deleted", "Structure deleted", "success");
        } catch (err) {
          setStructures((prev) => prev.filter((x) => x.id !== s.id));
          Swal.fire(
            "Deleted Locally",
            "Deleted locally (API not ready)",
            "info"
          );
        }
      }
    });
  };

  if (loading) return <div className="text-center p-5">Loading...</div>;

  /* ============================
     Render
  =============================*/
  return (
    <div style={{ background: "#eef6fd", minHeight: "100vh" }}>
      <div className="container py-4">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h3 className="fw-bold">Salary Structure</h3>

          <div className="d-flex gap-2">
            <button
              className="btn btn-outline-secondary"
              onClick={() => {
                loadData();
                loadPayrollItems();
              }}
            >
              Refresh
            </button>

            <button
              className="btn btn-primary"
              onClick={() => {
                setForm(blankForm);
                setShowAddForm(true);
                setEditId(null);
              }}
            >
              <i className="bi bi-plus-circle me-2" />
              Add New Structure
            </button>
          </div>
        </div>

        {usingSample && (
          <span className="badge bg-warning text-dark mb-3">
            Using Sample Data
          </span>
        )}

        {showAddForm && renderForm()}

        {structures.map((s) => (
          <div
            className="bg-white border rounded shadow-sm p-4 mb-3"
            key={s.id}
          >
            <div
              onClick={() => toggleExpand(s.id)}
              className="d-flex justify-content-between"
              style={{ cursor: "pointer" }}
            >
              <div>
                <h5 className="fw-bold">{s.name}</h5>
                <p className="text-muted">{s.description}</p>
                <p className="text-muted fw-semibold">
                  CTC Range:{" "}
                  {s.min_ctc && s.max_ctc
                    ? `${Number(s.min_ctc).toLocaleString()} - ${Number(
                        s.max_ctc
                      ).toLocaleString()}`
                    : "—"}
                </p>
                <p className="text-muted">
                  Effective From: {formatDate(s.effective_from)}
                </p>
              </div>

              <i
                className={`bi ${
                  expanded[s.id] ? "bi-chevron-up" : "bi-chevron-down"
                } fs-5`}
              />
            </div>

            {expanded[s.id] && (
              <>
                <hr />
                <div className="row">
                  <div className="col-md-6">
                    <h6 className="fw-bold">Earnings</h6>
                    {(s.components || [])
                      .filter((c) => c.component_type === "earning")
                      .map((e, i) => (
                        <div
                          key={i}
                          className="mb-2 d-flex align-items-center gap-2"
                        >
                          <i className="bi bi-plus-circle text-success" />
                          <div>
                            <div className="fw-semibold">
                              {e.component_name}
                            </div>
                            <small className="text-muted">
                              {e.calculation_type === "percentage"
                                ? `${e.value}% of ${e.percentage_of}`
                                : `${e.value} (fixed)`}
                            </small>
                          </div>
                        </div>
                      ))}
                  </div>

                  <div className="col-md-6">
                    <h6 className="fw-bold">Deductions</h6>
                    {(s.components || [])
                      .filter((c) => c.component_type === "deduction")
                      .map((d, i) => (
                        <div
                          key={i}
                          className="mb-2 d-flex align-items-center gap-2"
                        >
                          <i className="bi bi-dash-circle text-danger" />
                          <div>
                            <div className="fw-semibold">
                              {d.component_name}
                            </div>
                            <small className="text-muted">
                              {d.calculation_type === "percentage"
                                ? `${d.value}% of ${d.percentage_of}`
                                : `${d.value} (fixed)`}
                            </small>
                          </div>
                        </div>
                      ))}
                  </div>
                </div>

                <div className="d-flex justify-content-end mt-3 gap-3">
                  <button
                    className="btn btn-light"
                    onClick={() => startEdit(s)}
                  >
                    <i className="bi bi-pencil-square me-1" />
                    Edit
                  </button>

                  <button
                    className="btn btn-light text-danger"
                    onClick={() => confirmDelete(s)}
                  >
                    <i className="bi bi-trash me-1" />
                    Delete
                  </button>
                </div>
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );

  /* ============================
     Inline Form Renderer
  =============================*/
  function renderForm() {
    return (
      <div className="bg-white border rounded shadow-sm p-4 mb-3">
        <h5 className="fw-bold mb-3">
          {editId ? "Edit Structure" : "Add Structure"}
        </h5>

        <div className="row g-3">
          <div className="col-md-6">
            <label className="form-label fw-semibold">Name</label>
            <input
              className="form-control"
              value={form.name}
              onChange={(e) => handleFormChange("name", e.target.value)}
              placeholder="Level 1"
            />
          </div>

          <div className="col-md-6">
            <label className="form-label fw-semibold">Effective From</label>
            <input
              className="form-control"
              type="date"
              value={form.effective_from}
              onChange={(e) =>
                handleFormChange("effective_from", e.target.value)
              }
            />
          </div>

          <div className="col-md-6">
            <label className="form-label fw-semibold">Min CTC</label>
            <input
              className="form-control"
              type="number"
              value={form.min_ctc}
              onChange={(e) => handleFormChange("min_ctc", e.target.value)}
              placeholder="1200000"
            />
          </div>

          <div className="col-md-6">
            <label className="form-label fw-semibold">Max CTC</label>
            <input
              className="form-control"
              type="number"
              value={form.max_ctc}
              onChange={(e) => handleFormChange("max_ctc", e.target.value)}
              placeholder="1800000"
            />
          </div>

          <div className="col-md-12">
            <label className="form-label fw-semibold">Description</label>
            <textarea
              className="form-control"
              rows={2}
              value={form.description}
              onChange={(e) => handleFormChange("description", e.target.value)}
              placeholder="Short description"
            />
          </div>

          {/* Components list - Option A: single row per component */}
          <div className="col-md-12">
            <div className="d-flex justify-content-between align-items-center">
              <h6 className="fw-bold mt-2">Components</h6>
              <div>
                <button
                  className="btn btn-sm btn-outline-primary me-2"
                  onClick={addComponent}
                >
                  + Add Component
                </button>
                <button
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() =>
                    // add a sample simple component
                    setForm((p) => {
                      const seq = p.components.length
                        ? Math.max(
                            ...p.components.map((c) => c.sequence || 0)
                          ) + 1
                        : 1;
                      return {
                        ...p,
                        components: [
                          ...p.components,
                          {
                            component_name: "Basic",
                            component_type: "earning",
                            payroll_item_id: null,
                            payroll_item_code: "",
                            calculation_type: "percentage",
                            percentage_of: "ctc",
                            value: 40,
                            sequence: seq,
                            meta: {},
                          },
                        ],
                      };
                    })
                  }
                >
                  + Sample
                </button>
              </div>
            </div>

            {form.components.map((c, i) => (
              <div key={i} className="border rounded p-2 my-2 bg-light">
                <div className="row g-2 align-items-center">
                  {/* Component Name */}
                  <div className="col-md-3">
                    <label className="form-label mb-0">Component Name</label>
                    <input
                      className="form-control"
                      value={c.component_name}
                      onChange={(e) =>
                        handleComponentNameChange(i, e.target.value)
                      }
                      placeholder="Basic"
                    />
                  </div>

                  {/* Type - hidden from payroll item but locked if matched */}
                  <div className="col-md-2">
                    <label className="form-label mb-0">Type</label>
                    <select
                      className="form-select"
                      value={c.component_type || "earning"}
                      onChange={(e) =>
                        updateComponent(i, "component_type", e.target.value)
                      }
                      disabled={Boolean(c.payroll_item_id)}
                    >
                      <option value="earning">Earning</option>
                      <option value="deduction">Deduction</option>
                    </select>
                  </div>

                  {/* Calculation Type */}
                  <div className="col-md-2">
                    <label className="form-label mb-0">Calculation</label>
                    <select
                      className="form-select"
                      value={c.calculation_type}
                      onChange={(e) =>
                        updateComponent(i, "calculation_type", e.target.value)
                      }
                    >
                      <option value="percentage">Percentage</option>
                      <option value="fixed">Fixed</option>
                    </select>
                  </div>

                  {/* Percentage Of (only earnings shown) */}
                  <div className="col-md-3">
                    <label className="form-label mb-0">% Of</label>
                    <select
                      className="form-select"
                      value={c.percentage_of || ""}
                      disabled={c.calculation_type === "fixed"}
                      onChange={(e) =>
                        updateComponent(i, "percentage_of", e.target.value)
                      }
                    >
                      <option value="">Select base</option>
                      <option value="ctc">CTC</option>
                      {payrollItems
                        .filter((pi) => pi.type === "earning")
                        .map((pi) => (
                          <option key={pi.id} value={pi.code}>
                            {pi.code}
                          </option>
                        ))}
                    </select>
                  </div>

                  {/* Value */}
                  <div className="col-md-1">
                    <label className="form-label mb-0">Value</label>
                    <input
                      className="form-control"
                      type="number"
                      value={c.value}
                      onChange={(e) =>
                        updateComponent(i, "value", e.target.value)
                      }
                      placeholder={
                        c.calculation_type === "percentage" ? "40" : "1000"
                      }
                    />
                  </div>

                  {/* Remove */}
                  <div className="col-md-1 d-flex align-items-end">
                    <button
                      className="btn btn-sm btn-danger"
                      onClick={() =>
                        Swal.fire({
                          title: "Remove?",
                          icon: "warning",
                          showCancelButton: true,
                          confirmButtonText: "Remove",
                        }).then((res) => res.isConfirmed && removeComponent(i))
                      }
                    >
                      Remove
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="d-flex justify-content-end gap-2 mt-3">
          <button
            className="btn btn-secondary"
            onClick={() => {
              setShowAddForm(false);
              setEditId(null);
              setForm(blankForm);
            }}
            disabled={saving}
          >
            Cancel
          </button>

          <button
            className="btn btn-primary"
            onClick={saveStructure}
            disabled={saving}
          >
            {saving ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" />{" "}
                Saving...
              </>
            ) : editId ? (
              "Update Structure"
            ) : (
              "Save Structure"
            )}
          </button>
        </div>
      </div>
    );
  }
}
